/**
 * utils.js - Utility functions and constants for Three.js Configurator
 */

import anime from 'animejs';
import { _missingLabels } from './state.js';

// ===== GLOBALS ACCESS =====
export const getGlobals = () => window.GLOBALS || { tc: {} };

// ===== DECAL PROTECTION =====
/**
 * Check if a mesh allows decal placement
 * Meshes/Materials with "_noDecals" or "_protected" suffix in their name are protected
 * Also recognizes Blender export suffixes like .001, .002, etc.
 */
export function allowsDecals(object) {
    if (!object) return true;

    // Helper function to check name with optional Blender suffix (.001, .002, etc.)
    const hasProtectionMarker = (name) => {
        if (!name) return false;
        // Remove Blender export suffix (.001, .002, etc.) before checking
        const cleanName = name.replace(/\.\d+$/, '');
        return cleanName.includes('_noDecals') || cleanName.includes('_protected');
    };

    // Check object name
    if (hasProtectionMarker(object.name)) {
        return false;
    }

    // Check material name(s)
    if (object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const mat of materials) {
            if (mat && hasProtectionMarker(mat.name)) {
                return false;
            }
        }
    }

    return true;
}

// ===== ANIMATION UTILITIES =====
export function slideDown(el, duration = 200, easing = "linear", complete = function () {}, display = "block") {
    if (!el) return;
    anime.remove(el);

    // Remove inline display:none if present
    if (el.style.display === 'none') {
        el.style.display = '';
    }

    // Set display to block
    el.style.display = display;
    el.style.height = '';
    el.style.overflow = '';

    // Force reflow to ensure accurate height calculation
    el.offsetHeight;

    let heightSubnavi = el.scrollHeight;

    // If height is 0 or very small, wait a frame and recalculate
    if (heightSubnavi < 10) {
        requestAnimationFrame(() => {
            heightSubnavi = el.scrollHeight;

            // If still 0, set to 'auto' instead
            if (heightSubnavi < 10) {
                el.style.display = display;
                el.style.height = 'auto';
                el.style.visibility = '';
                complete();
                return;
            }

            startAnimation();
        });
        return;
    }

    startAnimation();

    function startAnimation() {
        // Kein el.style.visibility = "hidden" hier: height:0 + overflow:hidden blenden den Inhalt
        // schon vollständig aus. Die alte Version setzte visibility zusätzlich und verließ sich
        // darauf, dass anime()s "begin"-Callback sie wieder zurücksetzt - bricht eine spätere
        // slideUp/slideDown-Anfrage diese Animation vorher per anime.remove() ab (z.B. bei
        // schnellem Klicken), feuert "begin" nie und das Element bleibt dauerhaft unsichtbar.
        el.style.height = 0;
        el.style.overflow = "hidden";

        // Fallback: falls anime()s "complete" nie feuert (z.B. weil eine weitere slideUp/slideDown-
        // Anfrage für dasselbe Element die Animation per anime.remove() abbricht, bevor sie fertig
        // ist - beobachtet bei schnell aufeinanderfolgenden Klicks), zwingt dieser Timeout nach
        // Ablauf der Dauer trotzdem den Endzustand. done() ist idempotent, ein doppelter Aufruf
        // (durch complete UND Fallback) ist unschädlich.
        let finished = false;
        const done = () => {
            if (finished) return;
            finished = true;
            el.style.overflow = '';
            el.style.height = '';
            el.style.display = display;
            complete();
        };
        setTimeout(done, duration + 100);

        anime({
            targets: el,
            height: heightSubnavi,
            easing: easing,
            duration: duration,
            begin: function () {
                // Ensure display stays as block
                el.style.display = display;
            },
            complete: done
        });
    }
}

export function slideUp(el, duration = 200, easing = "linear", complete = function () {}) {
    if (!el) return;
    anime.remove(el);

    // Fallback wie in slideDown: erzwingt den Endzustand, falls "complete" durch eine
    // abgebrochene Animation (schnell aufeinanderfolgende Klicks) nie feuert.
    let finished = false;
    const done = () => {
        if (finished) return;
        finished = true;
        el.style.height = '';
        el.style.overflow = '';
        el.style.display = 'none';
        complete();
    };
    setTimeout(done, duration + 100);

    anime({
        targets: el,
        height: 0,
        easing: easing,
        duration: duration,
        begin: function () {
            el.style.visibility = '';
            el.style.overflow = "hidden";
        },
        complete: done
    });
}

// ===== LABEL FALLBACKS =====
export const labelFallbacks = {
    groups: {
        'default': 'Anhänger',
        'base': 'Grundaufbau',
        'glass': 'Verglasung',
        'railing': 'Geländer',
        'terrace': 'Terrasse'
    },
    sides: {
        'trailer': 'Anhänger',
        'floor': 'Boden',
        'front': 'Vorne',
        'back': 'Hinten',
        'left': 'Links',
        'right': 'Rechts',
        'middle': 'Mitte',
        'front_left': 'Vorne Links',
        'front_right': 'Vorne Rechts',
        'front_mid': 'Vorne Mitte',
        'back_left': 'Hinten Links',
        'back_right': 'Hinten Rechts',
        'back_mid': 'Hinten Mitte',
        'left_left': 'Linke Seite',
        'right_right': 'Rechte Seite'
    },
    combined: {
        'default_trailer': 'Anhänger',
        'default_floor': 'Boden',
        'base_front': 'Grundaufbau Vorne',
        'base_back': 'Grundaufbau Hinten',
        'base_middle': 'Grundaufbau Mitte',
        'base_front_left': 'Grundaufbau Vorne Links',
        'base_front_right': 'Grundaufbau Vorne Rechts',
        'base_front_mid': 'Grundaufbau Vorne Mitte',
        'base_back_left': 'Grundaufbau Hinten Links',
        'base_back_right': 'Grundaufbau Hinten Rechts',
        'base_back_mid': 'Grundaufbau Hinten Mitte',
        'base_left_left': 'Grundaufbau Linke Seite',
        'base_right_right': 'Grundaufbau Rechte Seite',
        'glass_front': 'Verglasung Vorne',
        'glass_back': 'Verglasung Hinten',
        'glass_front_left': 'Verglasung Vorne Links',
        'glass_front_right': 'Verglasung Vorne Rechts',
        'glass_front_mid': 'Verglasung Vorne Mitte',
        'glass_back_left': 'Verglasung Hinten Links',
        'glass_back_right': 'Verglasung Hinten Rechts',
        'glass_back_mid': 'Verglasung Hinten Mitte',
        'glass_left_left': 'Verglasung Linke Seite',
        'glass_right_right': 'Verglasung Rechte Seite'
    }
};

export function getLabelText(group, side = null) {
    const GLOBALS = getGlobals();

    if (!GLOBALS.tc) {
        if (!_missingLabels.has('_no_globals')) {
            _missingLabels.add('_no_globals');
        }
        return side === null
            ? (labelFallbacks.groups[group] || group)
            : (labelFallbacks.combined[`${group}_${side}`] || labelFallbacks.sides[side] || `${group} – ${side}`);
    }


    if (side === null) {
        const key = `label_group_${group}`;
        if (GLOBALS.tc[key]) {
            return GLOBALS.tc[key];
        }
        return labelFallbacks.groups[group] || group;
    }

    const keyCombined = `label_${group}_${side}`;
    if (GLOBALS.tc[keyCombined]) {
        return GLOBALS.tc[keyCombined];
    }

    const keySideOnly = `label_${side}`;
    if (GLOBALS.tc[keySideOnly]) {
        return GLOBALS.tc[keySideOnly];
    }

    return labelFallbacks.combined[`${group}_${side}`] || labelFallbacks.sides[side] || `${group} – ${side}`;
}

// ===== LOADER UTILITIES =====
export function showLoader(loaderEl, containerEl) {
    if (!loaderEl) return;

    loaderEl.style.display = 'flex';
    loaderEl.offsetHeight;
    requestAnimationFrame(() => {
        loaderEl.classList.remove('hidden');
    });

    const canvas = containerEl.querySelector('canvas');
    if (canvas) canvas.classList.remove('visible');
}

export function hideLoaderSmooth(loaderEl, containerEl) {
    if (!loaderEl) return;

    loaderEl.classList.add('hidden');

    const canvas = containerEl.querySelector('canvas');
    if (canvas) {
        setTimeout(() => canvas.classList.add('visible'), 300);
    }

    setTimeout(() => {
        loaderEl.style.display = 'none';
    }, 650);
}

// ===== CFG SLIDE ITEM UTILITIES =====

/**
 * Begrenzt die Höhe des Akkordeon-Inhalts, der gerade geöffnet wird, auf den tatsächlich
 * verfügbaren Platz - so bleiben alle (kollabierten) Headlines und die
 * .configuratorRequestPanel-Buttons darunter immer sichtbar, ohne dass das Panel selbst
 * scrollen muss. Ist der Inhalt kleiner als der verfügbare Platz, greift dieser Wert gar
 * nicht (slideDown animiert ohnehin nur auf die natürliche Inhaltshöhe) - das Panel bleibt
 * dann klein (z.B. bei "Farbauswahl" mit wenigen Einträgen).
 */
function applyDynamicContentMaxHeight(cfgSlideItem, content) {
    const panel = cfgSlideItem.closest('.configuratorPanel');
    if (!panel) return;

    const requestPanel = document.querySelector('.configuratorRequestPanel');
    const panelTop = panel.getBoundingClientRect().top;
    const bottomLimit = requestPanel
        ? requestPanel.getBoundingClientRect().top - 16
        : window.innerHeight - 16;

    let headlinesHeight = 0;
    panel.querySelectorAll('.cfgSlideItem__headline').forEach(h => {
        headlinesHeight += h.offsetHeight;
    });

    const available = bottomLimit - panelTop - headlinesHeight - 16;
    content.style.maxHeight = Math.max(available, 80) + 'px';
}

export function openCfgSlideItem(cfgSlideItem) {
    cfgSlideItem.classList.add("is-active");

    let content = cfgSlideItem.querySelector(".cfgSlideItem__content");
    if (content) {
        applyDynamicContentMaxHeight(cfgSlideItem, content);
        slideDown(content, 200, "linear", function () {
            cfgSlideItem.dispatchEvent(new CustomEvent('cfgslide:opened', { bubbles: true }));
        });
    } else {
        cfgSlideItem.dispatchEvent(new CustomEvent('cfgslide:opened', { bubbles: true }));
    }
}

export function closeCfgSlideItem(cfgSlideItem) {
    cfgSlideItem.classList.remove("is-active");

    let content = cfgSlideItem.querySelector(".cfgSlideItem__content");
    if (content) {
        slideUp(content, 200, "linear", function () {
            cfgSlideItem.dispatchEvent(new CustomEvent('cfgslide:closed', { bubbles: true }));
        });
    } else {
        cfgSlideItem.dispatchEvent(new CustomEvent('cfgslide:closed', { bubbles: true }));
    }
}

export function initCfgSlideItems(query, containerScope = null) {
    // Wenn kein Container angegeben ist, verwende .configuratorPanel
    const container = containerScope || document.querySelector('.configuratorPanel');
    if (!container) return;

    // If query is a string, use it as a selector, otherwise use default
    const headlines = typeof query === 'string'
        ? container.querySelectorAll(query + ' .cfgSlideItem__headline')
        : (query || container.querySelectorAll('.cfgSlideItem__headline'));


    headlines.forEach(el => {
        // Check if already initialized - skip if true to prevent duplicate listeners
        if (el.dataset.initialized === 'true') {
            return;
        }

        const cfgSlideItem = el.closest('.cfgSlideItem');
        if (!cfgSlideItem) return;

        // Mark as initialized before adding listener
        el.dataset.initialized = 'true';

        el.addEventListener("click", function cfgSlideClickHandler(e) {
            const content = cfgSlideItem.querySelector('.cfgSlideItem__content');

            // Check if slide is open by looking at content display and height
            const isOpen = content && (
                (content.style.display !== 'none' && content.style.display !== '') ||
                (content.offsetHeight > 0)
            );

            // Close other open slides
            container.querySelectorAll('.cfgSlideItem').forEach(openItem => {
                if (openItem !== cfgSlideItem) {
                    const otherContent = openItem.querySelector('.cfgSlideItem__content');
                    if (otherContent && otherContent.offsetHeight > 0) {
                        closeCfgSlideItem(openItem);
                    }
                }
            });

            if (isOpen) {
                closeCfgSlideItem(cfgSlideItem);
            } else {
                openCfgSlideItem(cfgSlideItem);
            }
        }, { passive: true });
    });
}

// ===== HELPER FUNCTIONS =====

// Gibt Materials immer als Array zurück (vereinfacht Material-Handling)
export function getMaterialsArray(material) {
    return Array.isArray(material) ? material : [material];
}

// Prüft ob ein Material ein Decal-Material ist (für Dispose-Checks)
export function isDecalMaterial(material) {
    return material?.userData?.decalUniforms?.decalMap;
}
